import { Injectable, inject } from "@angular/core";
import {IEnvironment} from "@shared/services/interfaces/environment.interface";
import {TranslateService} from "@ngx-translate/core";

@Injectable({
  providedIn: 'root',
})
export class SharedLanguageService {
  private _translateService = inject(TranslateService);

  languages_allowed: string[];

  constructor() {
    this.languages_allowed = ['de-DE', 'en-US'];
  }

  /**
   * @name setLanguage
   * @description
   * set the default lang and usage language
   * @memberof SharedLanguageService
   * @param language
   *
   */
  setLanguage(language: string | null | undefined): void {
    let langToUse: string;

    if (language && this.languages_allowed.includes(language)) {
      langToUse = language;
    } else if (this.languages_allowed.includes(navigator.language)) {
      langToUse = navigator.language;
    } else {
      langToUse = 'en-US';
    }

    this._translateService.setFallbackLang(langToUse);
    // this lane is to avoid errors to set the default language in case that is not english
    this._translateService.use(langToUse);
  }

  /**
   * @name setDefaultLanguageByEnvironment
   * @description
   * set the default language by environment specification
   * if the browserLang is one of the allowed languages it will set browserLang instead of environment
   * (this is used to the beginning of the application where there is still no token yet)
   * @memberof SharedLanguageService
   * @param environmentInc
   */
  setDefaultLanguageByEnvironment(environmentInc: IEnvironment): void {
    const browserLang = this.getBrowserLang();
    const defaultLang = this.languages_allowed.includes(browserLang)
      ? browserLang
      : environmentInc?.lang?.defaultLanguage ?? 'en-US';

    this.setLanguage(defaultLang);
  }

  /**
   * @name setLanguageByPriority
   * @description
   * set the language of the app taking in account a priority
   * @memberof SharedLanguageService
   * @param parameterLang
   * @param userLang
   * @param environment
   */
  setLanguageByPriority(
    parameterLang: string | undefined,
    userLang: string | null | undefined,
    environment: IEnvironment
  ): void {
    const defaultLang: string = environment.lang.defaultLanguage ?? 'en-US';
    const browserLang = this.getBrowserLang();

    const possibleLanguages = [parameterLang, userLang, browserLang];

    const validLanguage = possibleLanguages.find(
      (languages) =>
        languages && this.languages_allowed.some((allowed) => allowed.toLowerCase() === languages.toLowerCase())
    );

    const selectedLanguage =
      this.languages_allowed.find((allowed) => allowed.toLowerCase() === validLanguage?.toLowerCase()) || defaultLang;

    this.setLanguage(selectedLanguage);
  }

  /**
   * @name getCurrentLanguage
   * @description
   * return the default language set in the _translateService
   * @memberof SharedLanguageService
   */
  getCurrentLanguage(): string {
    return this._translateService.getCurrentLang() ?? this._translateService.getFallbackLang() ?? 'en-US';
  }

  /**
   * @name getBrowserLang
   * @description
   * return the browserLang
   * @memberof SharedLanguageService
   */
  getBrowserLang(): string {
    let browserLang = this._translateService.getBrowserLang();
    browserLang = browserLang?.length === 2 ? `${browserLang.toLowerCase()}-${browserLang.toUpperCase()}` : browserLang;
    return browserLang ?? 'en-US';
  }
}
